/**
 * The partner referral code a visitor arrived with, held from the link they
 * opened until there is a workspace to attach it to.
 *
 * A partner's link is `<FRONTEND_URL>/signup/<referral_code>`, or
 * `<FRONTEND_URL>/login/<referral_code>` for one issued before the generator
 * changed — so the code reaches this app as a path segment on a way-in screen
 * and nowhere else. Which of the two it was makes no difference past this
 * point: both park the code here. The only thing that can record it is
 * `POST /api/v1/onboarding/workspace/`, which runs much later:
 * after the email code or the Google round trip, after consent, after the
 * session goes live. Nothing in between can hold it — the visitor may not even
 * have an account yet — so it is parked here and read back at the one moment
 * it can be spent.
 *
 * This is the same gap `consentService` bridges for the DPDP ticks, and it is
 * bridged the same way on purpose: one pattern for "collected before the
 * account existed", not two.
 *
 * Session storage, not local. A referral followed and then abandoned should
 * not still be attributing the next person to use this browser to a partner
 * they never heard of. It has to survive a full page load either way: the
 * Google route leaves for Google and comes back as a fresh document, and Clerk
 * can finish even the email route with a top-level redirect, so nothing held
 * in memory would still be there by the time the workspace call runs.
 */

/** The longest code the backend will take; over this is a `400`. */
export const MAX_REFERRAL_CODE_LENGTH = 40;

const PENDING_REFERRAL_KEY = "naurist.pendingReferral";

/**
 * A code as it can be sent, or `null` for one that cannot.
 *
 * Length is the whole of the check, and it is here rather than at the call
 * site for a reason. This value comes off a URL, which means it comes from
 * whoever wrote the link — and the backend's contract is that an unknown code
 * is ignored and *never blocks onboarding*. A 200-character path segment would
 * break that promise by turning the workspace call into a `400`, so an
 * unusable code is dropped on the way in and the practice is simply created
 * unreferred.
 *
 * Nothing else is judged. Codes are matched case-insensitively by the backend,
 * so the case is left exactly as the partner's link spelled it, and an unknown
 * code is the server's to shrug at rather than this app's to guess about.
 */
export function normalizeReferralCode(
  raw: string | null | undefined
): string | null {
  const code = raw?.trim();
  if (!code) return null;
  return code.length <= MAX_REFERRAL_CODE_LENGTH ? code : null;
}

/**
 * Parks the code a visitor arrived with, for the workspace call to spend.
 *
 * Returns whether it was stored. Storage can throw outright — Safari private
 * mode, third-party cookie blocking in an embedded context — and a code that
 * cannot be parked is a referral that will not be recorded, so the caller is
 * told rather than it failing silently.
 *
 * A code that does not survive `normalizeReferralCode` clears whatever was
 * parked instead of overwriting it with something unusable.
 */
export function rememberReferralCode(raw: string | null | undefined): boolean {
  const code = normalizeReferralCode(raw);
  if (!code) {
    clearReferralCode();
    return false;
  }

  try {
    sessionStorage.setItem(PENDING_REFERRAL_KEY, code);
    return true;
  } catch {
    return false;
  }
}

/**
 * The parked code, or `null` when the visitor came to Naurist directly —
 * which is the ordinary case, and is what keeps every other sign-up free of
 * this without anything having to pass a flag down to it.
 *
 * Reading does not clear. The workspace call can fail and be retried, and a
 * code dropped on the first attempt would attribute the practice to nobody on
 * the second. `clearReferralCode` is called once the backend has answered.
 *
 * Re-normalised on the way out: this is user-writable storage, and the rule
 * that an unusable code must not break onboarding holds just as much for one
 * typed into devtools as for one off a link.
 */
export function readReferralCode(): string | null {
  try {
    return normalizeReferralCode(sessionStorage.getItem(PENDING_REFERRAL_KEY));
  } catch {
    return null;
  }
}

export function clearReferralCode(): void {
  try {
    sessionStorage.removeItem(PENDING_REFERRAL_KEY);
  } catch {
    /* nothing to clean up if it could not be written in the first place */
  }
}
